import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, Loader2, ShieldAlert, RefreshCw } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { SiteContentProvider } from "../data/SiteContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CART_KEY = "btw_pickup_cart_v1";
const SHOP_PHONE_DISPLAY = "(718) 297-4BTW";
const SHOP_PHONE_TEL = "7182974289";

// Poll config — chosen so Apple Pay / Google Pay redirects (which can lag
// the Stripe webhook by 20–30 s) still resolve to "paid" before we degrade
// to a friendlier "we're still checking" screen.
const NORMAL_POLL_ATTEMPTS = 30; // 30 × 2 s ≈ 60 s
const NORMAL_POLL_INTERVAL_MS = 2000;
// Retry backoff on network errors (server unreachable / timeout / CORS).
// We keep polling silently for a long time before ever showing a scary
// screen — because a paid Stripe charge is almost certainly captured on
// the server even if THIS browser can't reach the API for a bit.
const NETWORK_ERR_INTERVAL_MS = 3000;

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  // phase: polling | paid | pending | expired | unreachable
  const [status, setStatus] = useState({ phase: "polling", payload: null, error: null });
  const attempts = useRef(0);
  const netErrs = useRef(0);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      // A missing session_id genuinely means the user landed here without
      // going through checkout — safe to say they haven't been charged.
      setStatus({ phase: "no-session", payload: null, error: null });
      return;
    }
    cancelledRef.current = false;
    poll();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, [sessionId]);

  const schedule = (fn, ms) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(fn, ms);
  };

  const poll = async () => {
    if (cancelledRef.current) return;
    try {
      const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (cancelledRef.current) return;
      // Successful response — reset the network-error counter.
      netErrs.current = 0;
      if (data.payment_status === "paid") {
        setStatus({ phase: "paid", payload: data, error: null });
        try { localStorage.removeItem(CART_KEY); }
        catch (err) { console.warn("[cart] failed to clear cart after pay", err); }
        return;
      }
      if (data.status === "expired") {
        // Only Stripe telling us "expired" is a reliable signal that the
        // session was NOT paid. This is the one path where we can safely
        // tell the customer they haven't been charged.
        setStatus({ phase: "expired", payload: data, error: null });
        return;
      }
      attempts.current += 1;
      if (attempts.current >= NORMAL_POLL_ATTEMPTS) {
        setStatus({ phase: "pending", payload: data, error: null });
        return;
      }
      schedule(poll, NORMAL_POLL_INTERVAL_MS);
    } catch (e) {
      if (cancelledRef.current) return;
      netErrs.current += 1;
      // Never treat a network error as "not paid". Keep polling in the
      // background. Only show the safety-net "unreachable" screen (which
      // still tells the customer to NOT pay again) after ~30 s of failed
      // attempts, and even then we keep silently retrying.
      if (netErrs.current >= 10 && status.phase !== "unreachable") {
        setStatus({ phase: "unreachable", payload: null, error: e?.message || "Network error" });
      }
      schedule(poll, NETWORK_ERR_INTERVAL_MS);
    }
  };

  const forceRetry = () => {
    netErrs.current = 0;
    attempts.current = 0;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setStatus({ phase: "polling", payload: null, error: null });
    poll();
  };

  return (
    <SiteContentProvider>
      <div className="bg-[#0B0B0B] text-white min-h-screen">
        <Header />
        <main className="pt-20 sm:pt-24" data-testid="order-success-page">
          <section className="max-w-2xl mx-auto px-5 sm:px-8 py-16 sm:py-24 text-center">

            {status.phase === "polling" && (
              <>
                <Loader2 className="mx-auto animate-spin text-white/60" size={40} data-testid="oss-polling" />
                <h1 className="mt-6 font-display text-3xl sm:text-4xl uppercase">Confirming your payment…</h1>
                <p className="mt-3 text-white/55 text-sm">This takes just a moment.</p>
                <p className="mt-6 text-[11px] tracking-[0.2em] uppercase text-white/40">
                  Please <strong className="text-white">do not close this page or press back</strong> until we confirm.
                </p>
              </>
            )}

            {status.phase === "paid" && (
              <>
                <CheckCircle2 className="mx-auto text-[#A3D900]" size={56} data-testid="oss-paid" />
                <h1 className="mt-6 font-display text-4xl sm:text-5xl uppercase tracking-tight">Order Confirmed</h1>
                <p className="mt-3 text-white/65 text-base">
                  Thanks for ordering from BTW. We&apos;ve sent a confirmation to your email and our kitchen has been notified.
                </p>
                {status.payload?.order_id && (
                  <p className="mt-2 text-[11px] tracking-[0.3em] uppercase text-white/40" data-testid="order-id">
                    Order #{status.payload.order_id.slice(0, 8)}
                  </p>
                )}
                <Link to="/" className="mt-8 inline-flex items-center justify-center rounded-full bg-[#5FBDD6] hover:bg-[#3FA8C4] text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase px-7 py-3 text-xs">
                  Back to BTW
                </Link>
              </>
            )}

            {status.phase === "pending" && (
              <>
                <Clock className="mx-auto text-white/60" size={48} data-testid="oss-pending" />
                <h1 className="mt-6 font-display text-3xl uppercase">Payment received — confirming…</h1>
                <p className="mt-3 text-white/65 text-sm">
                  Your payment went through. We&apos;re just waiting for Stripe&apos;s final confirmation — this can
                  take a minute on busy days. <strong className="text-white">Do NOT pay again.</strong>{" "}
                  We&apos;ll email your receipt as soon as it lands.
                </p>
                {sessionId && (
                  <p className="mt-4 text-[10px] tracking-[0.3em] uppercase text-white/35" data-testid="oss-ref">
                    Reference: {sessionId.slice(0, 16)}…
                  </p>
                )}
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={forceRetry}
                    data-testid="oss-retry-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/50 text-white font-extrabold tracking-[0.18em] uppercase px-6 py-3 text-xs"
                  >
                    <RefreshCw size={14} /> Check again
                  </button>
                  <a href={`tel:${SHOP_PHONE_TEL}`} className="inline-flex items-center justify-center rounded-full bg-[#5FBDD6] hover:bg-[#3FA8C4] text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase px-6 py-3 text-xs">
                    Call {SHOP_PHONE_DISPLAY}
                  </a>
                </div>
              </>
            )}

            {/* Network / server unreachable — the biggest customer-safety
                path. We do NOT tell them the payment failed. We do NOT
                offer a "Return to Order Page" button that could cause a
                second charge. The background poll continues. */}
            {status.phase === "unreachable" && (
              <>
                <ShieldAlert className="mx-auto text-[#5FBDD6]" size={52} data-testid="oss-unreachable" />
                <h1 className="mt-6 font-display text-3xl sm:text-4xl uppercase tracking-tight">
                  Your Payment Is Safe
                </h1>
                <p className="mt-3 text-white/75 text-base">
                  We can&apos;t reach our server right now, but if Stripe charged you, your order is captured on
                  our side.{" "}
                  <strong className="text-yellow-300">
                    Please do NOT try to place the order again — you would be charged a second time.
                  </strong>
                </p>
                <p className="mt-3 text-white/60 text-sm">
                  We&apos;re still checking in the background. You can also verify by calling the restaurant
                  or checking your email in a few minutes for the BTW order confirmation.
                </p>
                {sessionId && (
                  <div className="mt-6 inline-block rounded-lg bg-white/[0.06] border border-white/15 px-4 py-3 text-left">
                    <div className="text-[9px] uppercase tracking-[0.3em] text-white/50 font-bold">Save this reference</div>
                    <div className="text-white text-sm font-mono break-all" data-testid="oss-unreachable-ref">
                      {sessionId}
                    </div>
                  </div>
                )}
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={forceRetry}
                    data-testid="oss-retry-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/50 text-white font-extrabold tracking-[0.18em] uppercase px-6 py-3 text-xs"
                  >
                    <RefreshCw size={14} /> Check again
                  </button>
                  <a href={`tel:${SHOP_PHONE_TEL}`} className="inline-flex items-center justify-center rounded-full bg-[#A3D900] hover:bg-[#8ebf00] text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase px-6 py-3 text-xs">
                    Call {SHOP_PHONE_DISPLAY}
                  </a>
                </div>
              </>
            )}

            {/* Stripe expired: reliable "not paid" signal. Only case where
                we tell the customer they can safely re-order. */}
            {status.phase === "expired" && (
              <>
                <Clock className="mx-auto text-yellow-400" size={48} data-testid="oss-expired" />
                <h1 className="mt-6 font-display text-3xl uppercase">Checkout session expired</h1>
                <p className="mt-3 text-white/65 text-sm">
                  You weren&apos;t charged — the payment window timed out before it was completed. Feel free to
                  start a new order.
                </p>
                <Link to="/order" className="mt-8 inline-flex items-center justify-center rounded-full bg-[#A3D900] hover:bg-[#8ebf00] text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase px-7 py-3 text-xs">
                  Start a new order
                </Link>
              </>
            )}

            {/* No session_id in URL at all — user landed here directly. */}
            {status.phase === "no-session" && (
              <>
                <Clock className="mx-auto text-white/60" size={48} data-testid="oss-no-session" />
                <h1 className="mt-6 font-display text-3xl uppercase">Nothing to confirm</h1>
                <p className="mt-3 text-white/55 text-sm">
                  It looks like you got here without checking out. Head back to the order page to place
                  an order.
                </p>
                <Link to="/order" className="mt-8 inline-flex items-center justify-center rounded-full bg-[#A3D900] hover:bg-[#8ebf00] text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase px-7 py-3 text-xs">
                  Go to order page
                </Link>
              </>
            )}

          </section>
        </main>
        <Footer />
      </div>
    </SiteContentProvider>
  );
}
